import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { ScrollView, Share, TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useMyReferral, useRedeemReferral } from '@/features/referral/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { buildReferralLink, normalizeReferralCode } from '@/lib/deeplink';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

export default function ReferralScreen() {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const palette = useThemePalette();
  const toast = useToast();
  const { userId } = useAuth();
  const refQ = useMyReferral();
  const redeem = useRedeemReferral();

  // Pre-fill the redeem field when arriving from an invite deep link
  // (tashkeela://referral/<CODE>). We never auto-redeem - the user taps Confirm.
  // The root layout pushes a fresh /referral screen per inbound link, so a lazy
  // initializer from the param covers both cold-start and warm taps.
  const params = useLocalSearchParams<{ code?: string }>();
  const [code, setCode] = useState(() => normalizeReferralCode(params.code) ?? '');

  const data = refQ.data;

  const onShare = () => {
    if (!data?.code) return;
    const link = buildReferralLink(data.code);
    void Share.share({
      message: t('referral.shareMessage', { code: data.code, link }),
    }).catch(() => {});
  };

  const onRedeem = () => {
    redeem.mutate(code.trim().toUpperCase(), {
      onSuccess: (r) => {
        toast.show({ message: t('referral.redeemed', { coins: formatNumber(r.reward, numeralOptions) }), tone: 'success' });
        setCode('');
      },
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  return (
    <Screen>
      <BackHeader title={t('referral.title')} />
      <ScrollView contentContainerStyle={{ paddingBottom: 36, paddingTop: 4 }}>
        <AppText variant="caption" className="mx-4 mb-3 text-center text-soft">
          {t('referral.intro')}
        </AppText>

        {!userId ? (
          <AppText variant="micro" className="mx-4 text-center text-muted">
            {t('referral.signInHint')}
          </AppText>
        ) : (
          <>
            <Card className="mx-4 mb-3 items-center gap-2 px-4 py-5">
              <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
                {t('referral.yourCode')}
              </AppText>
              {refQ.isPending ? (
                <Skeleton className="h-9 w-44" />
              ) : (
                <AppText weight="bold" tabular className="text-volt-bright" style={{ fontSize: 30, letterSpacing: 4 }}>
                  {data?.code ?? '--------'}
                </AppText>
              )}
              <AppText variant="micro" className="text-muted">
                {t('referral.invitedCount', { n: formatNumber(data?.invited_count ?? 0, numeralOptions) })}
              </AppText>
              <View className="mt-1 w-full">
                <Button label={t('referral.share')} onPress={onShare} />
              </View>
            </Card>

            {data?.redeemed ? (
              <Card className="mx-4 flex-row items-center justify-center gap-2 px-4 py-3.5">
                <Ionicons name="checkmark-circle" size={16} color={palette.win} />
                <AppText variant="caption" className="text-soft">
                  {t('referral.alreadyRedeemed')}
                </AppText>
              </Card>
            ) : (
              <Card className="mx-4 gap-3 px-4 py-4">
                <AppText variant="caption" weight="bold" className="text-ink">
                  {t('referral.enterCode')}
                </AppText>
                <TextInput
                  value={code}
                  onChangeText={(v) => setCode(v.toUpperCase())}
                  placeholder={t('referral.codePlaceholder')}
                  placeholderTextColor={palette.inactive}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  maxLength={8}
                  className="rounded-card border border-line bg-inset px-4 py-3"
                  style={{ color: palette.text, letterSpacing: 2, fontSize: 16 }}
                />
                <Button
                  label={t('referral.redeem')}
                  variant="secondary"
                  loading={redeem.isPending}
                  disabled={code.trim().length !== 8}
                  onPress={onRedeem}
                />
              </Card>
            )}
          </>
        )}
      </ScrollView>
    </Screen>
  );
}
